import type { FC } from 'react';
import { Flex, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { useCustomerForgotPassword } from '../../../../../bff-api/customers/useCustomerForgotPassword';
import { Field } from '../../../../../components/field';
import { Button, Text } from '../../../../../ui-components';
import { useForgotPasswordFields } from '../useForgotPasswordFields';
import type { UserLogin } from '../../login/Login';

export interface ForgotPasswordFormProps {
    form: UseFormReturn<UserLogin>;
    setSubmitted: (emailSent: boolean) => void;
}

const ForgotPasswordForm: FC<ForgotPasswordFormProps> = ({ form, setSubmitted }) => {
    const { isLoading, mutate } = useCustomerForgotPassword();
    const fields = useForgotPasswordFields({ form });

    const onSubmit = (userLogin: UserLogin): void => {
        mutate(
            { email: userLogin.email },
            {
                onSuccess: () => {
                    setSubmitted(true);
                },
            },
        );
    };

    return (
        <form
            data-testid="account-forgot-password-form"
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="w-full"
        >
            <VStack
                width="100%"
                padding={{ base: 4, sm: 8, md: 4 }}
                className="bg-white"
                borderRadius={{ md: 'lg' }}
                boxShadow="0px 2px 10px 0px rgba(65, 64, 66, 0.14)"
                spacing={0}
            >
                <Flex justifyContent="start" width="100%">
                    <Text style="bold" id="account-forgot-password-subtitle">
                        <FormattedMessage
                            id="account.forgot-password.subtitle"
                            defaultMessage="Request to reset password"
                        />
                    </Text>
                </Flex>
                <Flex justifyContent="start" width="100%" marginTop={4}>
                    <Text id="account-forgot-password-instructions">
                        <FormattedMessage
                            id="account.forgot-password.instructions"
                            defaultMessage="Enter the email address you signed up with and we'll send you instructions to reset your password."
                        />
                    </Text>
                </Flex>
                <Flex width="100%" marginTop={6}>
                    <Field {...fields.email} />
                </Flex>
                <Flex justifyContent="center" width="100%" marginTop={8}>
                    <Button
                        id="account-forgot-password-submit-button"
                        kind="submit"
                        variant="primary-black"
                        isLoading={isLoading}
                    >
                        <FormattedMessage
                            id="account.forgot-password.button-submit"
                            defaultMessage="Submit"
                        />
                    </Button>
                </Flex>
                <Flex justifyContent="center" width="100%" marginTop={4}>
                    <Button
                        id="account-forgot-password-back-to-previous"
                        variant="tertiary"
                        padding="0px"
                        onClick={(): void => {
                            // TODO: we should use react-router's history sadly it is not working as expected so we are using the global history.
                            // Seems like window.history.go(-1) is being hijacked by other process (my impression) and it's just reloading the page.
                            window.history.back();
                        }}
                    >
                        <Text as="span" style="bold">
                            <FormattedMessage
                                id="account.forgot-password.back-to-previous"
                                defaultMessage="Back to previous"
                            />
                        </Text>
                    </Button>
                </Flex>
            </VStack>
        </form>
    );
};

ForgotPasswordForm.propTypes = {
    setSubmitted: PropTypes.func.isRequired,
};

export { ForgotPasswordForm };
